import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';;
import { ContractPackChannel } from '@core/models/contract-pack-channel/contract-pack-channel.model';
import { FilteredItem } from '@core/models/filtered-item.model';
import { ThemaChannelGoal } from '@core/models/thema-channel-goal/thema-channel-goal.model';
import { ThemaChannelGoalService } from '@core/services/thema-channel-goal/thema-channel-goal.service';
import { Observable, of, Subject } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';

@Component({
standalone: false,
  selector: 'app-tv-select-pack-channels-dialog',
  templateUrl: './tv-select-pack-channels-dialog.component.html',
  styleUrls: ['./tv-select-pack-channels-dialog.component.scss'],
})
export class TvSelectPackChannelsDialogComponent implements OnInit {
  public panelOpened = false;

  public searchFormControl = new FormControl(null);
  public channelRateFormGroup: FormGroup;

  public selectAllOptions = false;
  public filteredOptionList$: Observable<any[]>;

  public isLoadingItemList: boolean = false;
  public filteredItemList$: Observable<any[]>;

  public selectedOptions: Set<any> = new Set();

  public isChannelListLoading: boolean = false;
  public channelsGoals$: Observable<ThemaChannelGoal[]>;

  componentDestroyed$: Subject<void> = new Subject();

  constructor(
    private fb: FormBuilder,
    public snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<TvSelectPackChannelsDialogComponent>,
    private themaChannelGoalService: ThemaChannelGoalService,
    @Inject(MAT_DIALOG_DATA) public data: { year: string; itemList: Observable<ContractPackChannel[]>; itemListAlreadySelected: any }
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.initSelectedItems();
    this.initFilteredItemList();
    this.initFilteredOptions();

    this.getChannelsGoals();
  }

  initSelectedItems() {
    this.data.itemList.subscribe(items => {
      items.map(item => {
        if (item.isSelected) this.selectedOptions.add(item);
      });
    });
  }

  initForm() {
    this.channelRateFormGroup = this.fb.group({
      rates: this.fb.array([]),
    });

    this.data.itemList.subscribe(items => {
      const rateControls = items.map(item =>
        this.fb.group({
          rate: new FormControl(
            {
              value: item.isSelected ? item.rate : 0,
              disabled: true,
            },
            [Validators.required, Validators.min(1), Validators.max(100)]
          ),
          item: new FormControl(item),
        })
      );

      this.channelRateFormGroup.setControl('rates', this.fb.array(rateControls));
    });
  }

  get rates(): FormArray {
    return this.channelRateFormGroup.get('rates') as FormArray;
  }

  getRateFormControl(index: number): FormControl {
    return this.rates.at(index).get('rate') as FormControl;
  }

  getRateFormControlValue(index: number) {
    return this.rates.at(index).value.rate;
  }

  setRateFormControlValue(index: number, value: number) {
    this.rates.at(index).get('rate').setValue(value);
  }

  public getChannelsGoals(): void {
    this.themaChannelGoalService.getList(Number(this.data.year)).subscribe((goals: ThemaChannelGoal[]) => {
      this.isChannelListLoading = false;
      this.channelsGoals$ = of(goals);
    });
  }

  private initFilteredOptions() {
    this.filteredOptionList$ = this.data.itemList;
  }

  initFilteredItemList() {
    // Filtrage des éléments en fonction de la recherche
    this.filteredItemList$ = this.searchFormControl.valueChanges.pipe(
      startWith(''), // Démarrer avec une chaîne vide
      switchMap(searchTerm => this.filterList(searchTerm))
    );
  }

  // Méthode qui retourne un Observable filtré
  filterList(searchTerm: string): Observable<any[]> {
    const lowerCaseTerm = searchTerm.toLowerCase();

    // Filtrer la liste en fonction du terme de recherche
    return this.data.itemList.pipe(
      map(items =>
        items.filter(item => item.channel.id?.toString().includes(lowerCaseTerm) || item.channel.name.toLowerCase().includes(lowerCaseTerm))
      )
    );
  }

  public displayItem(item: FilteredItem): string {
    return item?.name && item?.id ? `${item.name} (${item.id})` : '';
  }

  onSelectAllChange(event: any) {
    this.selectAllOptions = event.checked;

    this.filteredOptionList$?.subscribe(options => {
      options.forEach(item => {
        if (this.selectAllOptions) {
          this.selectedOptions.add(item);
        } else if (!this.selectAllOptions) {
          this.selectedOptions.delete(item);
        }
      });
    });

    this.updateChannelsRate();
  }

  isOptionSelected(item) {
    return this.selectedOptions.has(item);
  }

  onOptionChange(event: any, item: any, i = null) {
    const itemPackChannel: ContractPackChannel = { ...item };

    if (event.checked) {
      if (!this.selectedOptions.has(item)) {
        itemPackChannel.rate = this.getRateFormControlValue(i);
        this.selectedOptions.add(itemPackChannel);
      }
    } else {
      this.selectedOptions.forEach(option => {
        this.setRateFormControlValue(i, 0);
        if (option.channel.id === itemPackChannel.channel.id) {
          this.selectedOptions.delete(option);
        }
      });
    }

    // Recalcul
    this.updateChannelsRate();
  }

  onRateChange(event: any, item: any, i = null) {
    this.selectedOptions.forEach(option => {
      if (option.channel.id === item.channel.id) {
        this.selectedOptions.delete(option);
      }
    });
    const itemPackChannel: ContractPackChannel = { ...item };
    itemPackChannel.rate = this.getRateFormControlValue(i) || 0;
    this.selectedOptions.add(itemPackChannel);
  }

  onSaveChosenItemList() {
    if (this.channelRateFormGroup.valid) {
      if (this.getTotalSelectedRepartition() <= 100) {
        this.dialogRef.close(this.selectedOptions);
      } else {
        this.snackBar.open('La répartition totale ne doit pas dépasser 100 %', null, {
          duration: 4000,
          verticalPosition: 'top',
          panelClass: ['tv-chip-error'],
        });
      }
    } else {
      this.snackBar.open('Veuillez corriger le formulaire', null, {
        duration: 4000,
        verticalPosition: 'top',
        panelClass: ['tv-chip-error'],
      });
    }
  }

  onCancelSelectClick() {
    this.selectedOptions.clear();
    this.dialogRef.close();
  }

  getTotalSelectedRepartition() {
    let repartition = 0;
    // this.selectedOptions.forEach(option => Math.round((repartition += option.rate) * 10) / 10);
    this.selectedOptions.forEach(option => Math.round((repartition += option.rate || 0)));
    return parseFloat(repartition.toFixed(0));
  }

  public getTotalRate(): number {
    const rawSum = Array.from(this.selectedOptions).reduce((sum, selectedChannel) => sum + selectedChannel.rate, 0);

    return Math.round(rawSum * 10) / 10;
  }

  public updateChannelsRate() {
    // get the sum of all the selected channels goals
    const totalSelectedGoals = this.channelsGoals$.pipe(
      map(channels =>
        channels.reduce((sum, channelGoal) => {
          if (Array.from(this.selectedOptions).some(selectedChannel => selectedChannel.channel.channelCode === channelGoal.channelCode)) {
            return sum + channelGoal.goal;
          }
          return sum;
        }, 0)
      )
    );

    // recalculate the rate of the selected channels
    this.channelsGoals$.subscribe(channelsGoals => {
      totalSelectedGoals.subscribe(total => {
        if (total > 0) {
          // this.selectedOptions.add(
          //   Array.from(this.selectedOptions).map(selectedChannel => {
          //     const channelGoal = channelsGoals.find(goal => goal.channelCode === selectedChannel.channel.channelCode);
          //     if (channelGoal) {
          //       // get the rate of the channel at 1 decimal
          //       selectedChannel.rate = Math.round((channelGoal.goal / total) * 10000) / 100;
          //       this.updateSelectedChannelRate(selectedChannel, selectedChannel.rate);
          //     }
          //
          //     return selectedChannel;
          //   })
          // );
          Array.from(this.selectedOptions).map(selectedChannel => {
            const channelGoal = channelsGoals.find(goal => goal.channelCode === selectedChannel.channel.channelCode);
            if (channelGoal) {
              // get the rate of the channel at 1 decimal
              selectedChannel.rate = Math.round((channelGoal.goal / total) * 10000) / 100;
            }
          });
        }

        // find if total is greater or lower than 100
        if (this.getTotalRate() !== 100 && this.selectedOptions.size > 0) {
          // find the channel with the highest rate and set the rate to match 100
          const maxRateChannel = Array.from(this.selectedOptions).reduce((prev, current) => (prev.rate > current.rate ? prev : current));

          const totalRate = this.getTotalRate();
          const diff = Math.round((totalRate > 100 ? totalRate - 100 : 100 - totalRate) * 10) / 10;

          maxRateChannel.rate = totalRate > 100 ? maxRateChannel.rate - diff : maxRateChannel.rate + diff;
          // this.updateSelectedChannelRate(maxRateChannel, maxRateChannel.rate);
        }
      });
    });

    // Refresh display values
    this.refreshDisplayedSelectedRates();
  }

  // public updateSelectedChannelRate(channel: ContractPackChannel, newRate: number) {
  //   const index = Array.from(this.selectedOptions).findIndex(item => item.id === channel.id);
  //   if (index !== -1) {
  //     let arraySelectedChannels = Array.from(this.selectedOptions);
  //     arraySelectedChannels[index] = { ...channel, rate: newRate };
  //     arraySelectedChannels = [...arraySelectedChannels];
  //     // this.selectedOptions = new Set(arraySelectedChannels);
  //   }
  // }

  private refreshDisplayedSelectedRates() {
    this.rates.controls.map(control => {
      Array.from(this.selectedOptions).find(selectedChannel => {
        if (control.get('item').value.channel.id === selectedChannel.channel.id) {
          control.get('rate').setValue(selectedChannel.rate || 0);
          control.get('item').setValue(selectedChannel);
        }
      });
    });
  }
}
